import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Chip, Button, IconButton, Tooltip, Tabs, Tab, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert, Autocomplete,
  LinearProgress,
} from '@mui/material';
import {
  Hotel as HotelIcon, MeetingRoom as RoomIcon, Bed as BedIcon, PersonPin as OccupiedIcon,
  EventAvailable as VacantIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ExitToApp as VacateIcon, PersonAdd as AllotIcon, DirectionsWalk as OutpassIcon,
  AssignmentReturn as ReturnIcon, CheckCircle as ApproveIcon,
  Cancel as RejectIcon, ReceiptLong as InvoiceIcon, PlaylistAddCheck as GenerateIcon,
  PaidOutlined as PaidIcon,
} from '@mui/icons-material';
import { hostelAPI, studentAPI } from '../services/api';
import { StatCard } from './accounting/common';

const emptyHostel = { name: '', hostelType: 'GIRLS', wardenName: '', wardenPhone: '', monthlyFee: '', address: '' };
const emptyRoom = { hostelId: '', roomNumber: '', floor: '', capacity: 3, roomType: '' };

export default function HostelManagement() {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState({});
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allotments, setAllotments] = useState([]);
  const [students, setStudents] = useState([]);
  const [outpasses, setOutpasses] = useState([]);
  const [roomChangeRequests, setRoomChangeRequests] = useState([]);
  const [feeInvoices, setFeeInvoices] = useState([]);
  const [roomFilter, setRoomFilter] = useState('');
  const [hostelDlg, setHostelDlg] = useState(null);   // { id?, ...form }
  const [roomDlg, setRoomDlg] = useState(null);
  const [allotDlg, setAllotDlg] = useState(null);     // { room, student, fee }
  const [outpassDlg, setOutpassDlg] = useState(null); // { student, outDate, expectedReturnDate, destination, reason, contactNumber }
  const [roomChangeReject, setRoomChangeReject] = useState(null); // { request, remarks }
  const [feeGenDlg, setFeeGenDlg] = useState(null);   // { month, year }
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });
  const errorText = (e, fallback) => e?.response?.data?.message || fallback;

  const load = async () => {
    try {
      const [s, h, r, a, st, op, rc, fi] = await Promise.all([
        hostelAPI.getSummary(), hostelAPI.getHostels(), hostelAPI.getRooms(),
        hostelAPI.getAllotments('ACTIVE'), studentAPI.getAll(),
        hostelAPI.getOutpasses(), hostelAPI.getRoomChangeRequests(), hostelAPI.getFeeInvoices({}),
      ]);
      setSummary(s.data); setHostels(h.data); setRooms(r.data); setAllotments(a.data); setStudents(st.data);
      setOutpasses(op.data); setRoomChangeRequests(rc.data); setFeeInvoices(fi.data);
    } catch (e) { notify(errorText(e, 'Could not load hostel data'), 'error'); }
  };
  useEffect(() => { load(); }, []);

  const freeStudents = useMemo(() => {
    const taken = new Set(allotments.map(a => a.studentId));
    return students.filter(s => !taken.has(s.id));
  }, [students, allotments]);

  const visibleRooms = roomFilter ? rooms.filter(r => String(r.hostelId) === String(roomFilter)) : rooms;

  // ── hostels
  const saveHostel = async () => {
    const d = hostelDlg;
    if (!d.name.trim()) return notify('Hostel name is required', 'error');
    try {
      const body = { ...d, monthlyFee: d.monthlyFee === '' ? null : d.monthlyFee };
      if (d.id) await hostelAPI.updateHostel(d.id, body); else await hostelAPI.createHostel(body);
      setHostelDlg(null); notify('Hostel saved'); load();
    } catch (e) { notify(errorText(e, 'Could not save hostel'), 'error'); }
  };
  const deleteHostel = async (h) => {
    if (!window.confirm(`Delete ${h.name}?`)) return;
    try { await hostelAPI.deleteHostel(h.id); notify('Hostel deleted'); load(); }
    catch (e) { notify(errorText(e, 'Could not delete hostel'), 'error'); }
  };

  // ── rooms
  const saveRoom = async () => {
    const d = roomDlg;
    if (!d.hostelId || !String(d.roomNumber).trim() || !d.capacity) return notify('Hostel, room number and capacity are required', 'error');
    try {
      if (d.id) await hostelAPI.updateRoom(d.id, d); else await hostelAPI.createRoom(d);
      setRoomDlg(null); notify('Room saved'); load();
    } catch (e) { notify(errorText(e, 'Could not save room'), 'error'); }
  };
  const deleteRoom = async (r) => {
    if (!window.confirm(`Delete room ${r.roomNumber}?`)) return;
    try { await hostelAPI.deleteRoom(r.id); notify('Room deleted'); load(); }
    catch (e) { notify(errorText(e, 'Could not delete room'), 'error'); }
  };

  // ── allotments
  const allot = async () => {
    if (!allotDlg.student) return notify('Choose a student', 'error');
    try {
      await hostelAPI.allot({ studentId: allotDlg.student.id, roomId: allotDlg.room.id, monthlyFee: allotDlg.fee === '' ? undefined : allotDlg.fee });
      setAllotDlg(null); notify('Room allotted'); load();
    } catch (e) { notify(errorText(e, 'Could not allot room'), 'error'); }
  };
  const vacate = async (a) => {
    if (!window.confirm(`Vacate ${a.studentName} from room ${a.roomNumber}?`)) return;
    try { await hostelAPI.vacate(a.id); notify('Room vacated'); load(); }
    catch (e) { notify(errorText(e, 'Could not vacate'), 'error'); }
  };

  // ── outpasses
  const saveOutpass = async () => {
    const d = outpassDlg;
    if (!d.student) return notify('Choose a student', 'error');
    if (!d.expectedReturnDate) return notify('Enter the expected return date', 'error');
    try {
      await hostelAPI.createOutpass({
        studentId: d.student.studentId, outDate: d.outDate || undefined, expectedReturnDate: d.expectedReturnDate,
        destination: d.destination, reason: d.reason, contactNumber: d.contactNumber,
      });
      setOutpassDlg(null); notify('Outpass created'); load();
    } catch (e) { notify(errorText(e, 'Could not create outpass'), 'error'); }
  };
  const markReturned = async (o) => {
    if (!window.confirm(`Mark ${o.studentName} as returned?`)) return;
    try { await hostelAPI.markReturned(o.id); notify('Marked as returned'); load(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  // ── room change requests
  const approveRoomChange = async (r) => {
    if (!window.confirm(`Approve the move to room ${r.preferredRoomNumber || '(unspecified)'} for ${r.studentName}?`)) return;
    try { await hostelAPI.approveRoomChange(r.id); notify('Room change approved and allotted'); load(); }
    catch (e) { notify(errorText(e, 'Could not approve'), 'error'); }
  };
  const rejectRoomChange = async () => {
    try {
      await hostelAPI.rejectRoomChange(roomChangeReject.request.id, roomChangeReject.remarks);
      setRoomChangeReject(null); notify('Request rejected'); load();
    } catch (e) { notify(errorText(e, 'Could not reject'), 'error'); }
  };

  // ── hostel fee invoices
  const generateFeeInvoices = async () => {
    try {
      const res = await hostelAPI.generateFeeInvoices({ month: feeGenDlg.month, year: feeGenDlg.year });
      setFeeGenDlg(null); notify(res.data.message || 'Invoices generated'); load();
    } catch (e) { notify(errorText(e, 'Could not generate invoices'), 'error'); }
  };
  const markInvoicePaid = async (inv) => {
    try { await hostelAPI.markFeeInvoicePaid(inv.id, 'CASH'); notify('Marked as paid'); load(); }
    catch (e) { notify(errorText(e, 'Could not update'), 'error'); }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#004d40' }}>Hostel</Typography>
        <Typography variant="body2" color="text.secondary">Hostels, rooms, allotments, outpasses, room requests and hostel fees in one place</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Hostels" value={summary.hostels ?? 0} icon={HotelIcon} gradient="linear-gradient(135deg,#004d40,#00897b)" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Rooms" value={summary.rooms ?? 0} icon={RoomIcon} gradient="linear-gradient(135deg,#0288d1,#26c6da)" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Beds" value={summary.beds ?? 0} icon={BedIcon} gradient="linear-gradient(135deg,#6a1b9a,#ab47bc)" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Occupied" value={summary.occupied ?? 0} icon={OccupiedIcon} gradient="linear-gradient(135deg,#e65100,#ff9800)"
            subtitle={summary.beds ? `${Math.round((summary.occupied / summary.beds) * 100)}% occupancy` : undefined} />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatCard title="Vacant beds" value={summary.vacant ?? 0} icon={VacantIcon} gradient="linear-gradient(135deg,#2e7d32,#66bb6a)" />
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ bgcolor: '#f8f9fa', '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}>
          <Tab label={`Allotments (${allotments.length})`} />
          <Tab label={`Rooms (${rooms.length})`} />
          <Tab label={`Hostels (${hostels.length})`} />
          <Tab label={`Outpasses (${outpasses.filter(o => o.status === 'OUT').length})`} />
          <Tab label={`Room requests (${roomChangeRequests.filter(r => r.status === 'PENDING').length})`} />
          <Tab label="Hostel fees" />
        </Tabs>
      </Paper>

      {/* ── Allotments ── */}
      {tab === 0 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Student</TableCell><TableCell>Admission No</TableCell><TableCell>Program / Year</TableCell>
                <TableCell>Hostel</TableCell><TableCell>Room</TableCell><TableCell>Since</TableCell>
                <TableCell align="right">Monthly fee</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {allotments.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No students are staying in the hostel yet. Use the Rooms tab to allot a room.</TableCell></TableRow>}
                {allotments.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{a.studentName}</TableCell>
                    <TableCell>{a.admissionNo}</TableCell>
                    <TableCell>{a.program || '-'}</TableCell>
                    <TableCell>{a.hostelName}</TableCell>
                    <TableCell><Chip size="small" label={a.roomNumber} /></TableCell>
                    <TableCell>{a.fromDate}</TableCell>
                    <TableCell align="right">{a.monthlyFee != null ? `₹${Number(a.monthlyFee).toLocaleString('en-IN')}` : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Vacate room"><IconButton size="small" color="warning" onClick={() => vacate(a)}><VacateIcon /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Rooms ── */}
      {tab === 1 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField select size="small" label="Hostel" value={roomFilter} onChange={e => setRoomFilter(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">All hostels</MenuItem>
              {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} disabled={hostels.length === 0}
              onClick={() => setRoomDlg({ ...emptyRoom, hostelId: roomFilter || hostels[0]?.id || '' })}>Add room</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Hostel</TableCell><TableCell>Room</TableCell><TableCell>Floor</TableCell><TableCell>Type</TableCell>
                <TableCell sx={{ width: 200 }}>Occupancy</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {visibleRooms.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No rooms yet</TableCell></TableRow>}
                {visibleRooms.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.hostelName}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.roomNumber}</TableCell>
                    <TableCell>{r.floor || '-'}</TableCell>
                    <TableCell>{r.roomType}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={r.capacity ? (r.occupied / r.capacity) * 100 : 0}
                          color={r.vacant === 0 ? 'warning' : 'success'} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                        <Typography variant="caption">{r.occupied}/{r.capacity}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={r.vacant === 0 ? 'Room is full' : 'Allot a student'}>
                        <span><IconButton size="small" color="primary" disabled={r.vacant === 0}
                          onClick={() => setAllotDlg({ room: r, student: null, fee: '' })}><AllotIcon /></IconButton></span>
                      </Tooltip>
                      <IconButton size="small" onClick={() => setRoomDlg({ ...r })}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteRoom(r)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Hostels ── */}
      {tab === 2 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex' }}>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setHostelDlg({ ...emptyHostel })}>Add hostel</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Name</TableCell><TableCell>Type</TableCell><TableCell>Warden</TableCell><TableCell>Phone</TableCell>
                <TableCell align="right">Monthly fee</TableCell><TableCell>Rooms</TableCell><TableCell>Beds (occupied)</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {hostels.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hostels yet</TableCell></TableRow>}
                {hostels.map(h => (
                  <TableRow key={h.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{h.name}</TableCell>
                    <TableCell><Chip size="small" label={h.hostelType === 'BOYS' ? 'Boys' : 'Girls'} color={h.hostelType === 'BOYS' ? 'primary' : 'secondary'} /></TableCell>
                    <TableCell>{h.wardenName || '-'}</TableCell>
                    <TableCell>{h.wardenPhone || '-'}</TableCell>
                    <TableCell align="right">{h.monthlyFee != null ? `₹${Number(h.monthlyFee).toLocaleString('en-IN')}` : '-'}</TableCell>
                    <TableCell>{h.rooms}</TableCell>
                    <TableCell>{h.beds} ({h.occupied})</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setHostelDlg({ ...h, monthlyFee: h.monthlyFee ?? '' })}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteHostel(h)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Outpasses ── */}
      {tab === 3 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex' }}>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<OutpassIcon />} disabled={allotments.length === 0}
              onClick={() => setOutpassDlg({ student: null, outDate: '', expectedReturnDate: '', destination: '', reason: '', contactNumber: '' })}>
              New outpass
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Student</TableCell><TableCell>Out date</TableCell><TableCell>Expected return</TableCell>
                <TableCell>Destination</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {outpasses.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No outpasses recorded yet</TableCell></TableRow>}
                {outpasses.map(o => (
                  <TableRow key={o.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{o.studentName}<Typography variant="caption" display="block" color="text.secondary">{o.admissionNo}</Typography></TableCell>
                    <TableCell>{o.outDate}{o.outTime ? ` ${o.outTime}` : ''}</TableCell>
                    <TableCell>{o.expectedReturnDate}</TableCell>
                    <TableCell>{o.destination || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={o.status === 'OUT' ? (o.overdue ? 'Overdue' : 'Out') : 'Returned'}
                        color={o.status === 'OUT' ? (o.overdue ? 'error' : 'warning') : 'success'} />
                    </TableCell>
                    <TableCell align="right">
                      {o.status === 'OUT' && (
                        <Tooltip title="Mark returned"><IconButton size="small" color="success" onClick={() => markReturned(o)}><ReturnIcon /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Room change requests ── */}
      {tab === 4 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Student</TableCell><TableCell>Current room</TableCell><TableCell>Preferred room</TableCell>
                <TableCell>Reason</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {roomChangeRequests.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No room change requests</TableCell></TableRow>}
                {roomChangeRequests.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.studentName}<Typography variant="caption" display="block" color="text.secondary">{r.admissionNo}</Typography></TableCell>
                    <TableCell>{r.currentRoomNumber || '-'}</TableCell>
                    <TableCell>{r.preferredRoomNumber || '(not specified)'}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}><Typography variant="body2" noWrap>{r.reason}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={r.status === 'APPROVED' ? 'success' : r.status === 'PENDING' ? 'warning' : 'error'} />
                    </TableCell>
                    <TableCell align="right">
                      {r.status === 'PENDING' && (
                        <>
                          <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => approveRoomChange(r)}><ApproveIcon /></IconButton></Tooltip>
                          <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => setRoomChangeReject({ request: r, remarks: '' })}><RejectIcon /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Hostel fee invoices ── */}
      {tab === 5 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex' }}>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" startIcon={<GenerateIcon />}
              onClick={() => setFeeGenDlg({ month: new Date().getMonth() + 1, year: new Date().getFullYear() })}>
              Generate this month's invoices
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Student</TableCell><TableCell>Month / Year</TableCell><TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {feeInvoices.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hostel fee invoices yet. Use "Generate this month's invoices".</TableCell></TableRow>}
                {feeInvoices.map(f => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{f.studentName}<Typography variant="caption" display="block" color="text.secondary">{f.admissionNo}</Typography></TableCell>
                    <TableCell>{f.month}/{f.year}</TableCell>
                    <TableCell align="right">{f.amount != null ? `₹${Number(f.amount).toLocaleString('en-IN')}` : '-'}</TableCell>
                    <TableCell><Chip size="small" icon={<InvoiceIcon fontSize="small" />} label={f.status} color={f.status === 'PAID' ? 'success' : 'warning'} /></TableCell>
                    <TableCell align="right">
                      {f.status !== 'PAID' && (
                        <Tooltip title="Mark paid"><IconButton size="small" color="success" onClick={() => markInvoicePaid(f)}><PaidIcon /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Hostel dialog */}
      <Dialog open={!!hostelDlg} onClose={() => setHostelDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{hostelDlg?.id ? 'Edit hostel' : 'Add hostel'}</DialogTitle>
        <DialogContent dividers>
          {hostelDlg && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12} sm={8}><TextField fullWidth required label="Hostel name" value={hostelDlg.name} onChange={e => setHostelDlg({ ...hostelDlg, name: e.target.value })} /></Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Type" value={hostelDlg.hostelType || 'GIRLS'} onChange={e => setHostelDlg({ ...hostelDlg, hostelType: e.target.value })}>
                  <MenuItem value="GIRLS">Girls</MenuItem><MenuItem value="BOYS">Boys</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Warden name" value={hostelDlg.wardenName || ''} onChange={e => setHostelDlg({ ...hostelDlg, wardenName: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Warden phone" value={hostelDlg.wardenPhone || ''} onChange={e => setHostelDlg({ ...hostelDlg, wardenPhone: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="number" label="Monthly fee (₹)" value={hostelDlg.monthlyFee} onChange={e => setHostelDlg({ ...hostelDlg, monthlyFee: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Address / location" value={hostelDlg.address || ''} onChange={e => setHostelDlg({ ...hostelDlg, address: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setHostelDlg(null)}>Cancel</Button><Button variant="contained" onClick={saveHostel}>Save</Button></DialogActions>
      </Dialog>

      {/* Room dialog */}
      <Dialog open={!!roomDlg} onClose={() => setRoomDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{roomDlg?.id ? 'Edit room' : 'Add room'}</DialogTitle>
        <DialogContent dividers>
          {roomDlg && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <TextField select fullWidth required label="Hostel" value={roomDlg.hostelId} disabled={!!roomDlg.id} onChange={e => setRoomDlg({ ...roomDlg, hostelId: e.target.value })}>
                  {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth required label="Room number" value={roomDlg.roomNumber} disabled={!!roomDlg.id} onChange={e => setRoomDlg({ ...roomDlg, roomNumber: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Floor" value={roomDlg.floor || ''} onChange={e => setRoomDlg({ ...roomDlg, floor: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth required type="number" label="Capacity (beds)" inputProps={{ min: 1 }} value={roomDlg.capacity} onChange={e => setRoomDlg({ ...roomDlg, capacity: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Room type" placeholder="e.g. 3-Sharing" value={roomDlg.roomType || ''} onChange={e => setRoomDlg({ ...roomDlg, roomType: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setRoomDlg(null)}>Cancel</Button><Button variant="contained" onClick={saveRoom}>Save</Button></DialogActions>
      </Dialog>

      {/* Allot dialog */}
      <Dialog open={!!allotDlg} onClose={() => setAllotDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Allot room {allotDlg?.room.roomNumber} ({allotDlg?.room.hostelName})</DialogTitle>
        <DialogContent dividers>
          {allotDlg && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Autocomplete options={freeStudents} value={allotDlg.student}
                  getOptionLabel={s => `${s.firstName} ${s.lastName} (${s.admissionNo})`}
                  onChange={(e, v) => setAllotDlg({ ...allotDlg, student: v })}
                  renderInput={params => <TextField {...params} label="Student (not yet in a hostel)" />} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth type="number" label="Monthly fee (₹) — leave blank for the hostel's default" value={allotDlg.fee} onChange={e => setAllotDlg({ ...allotDlg, fee: e.target.value })} />
              </Grid>
              <Grid item xs={12}><Alert severity="info">{allotDlg.room.vacant} bed(s) free in this room.</Alert></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setAllotDlg(null)}>Cancel</Button><Button variant="contained" onClick={allot}>Allot</Button></DialogActions>
      </Dialog>

      {/* Outpass dialog */}
      <Dialog open={!!outpassDlg} onClose={() => setOutpassDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>New outpass</DialogTitle>
        <DialogContent dividers>
          {outpassDlg && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Autocomplete options={allotments} value={outpassDlg.student}
                  getOptionLabel={a => `${a.studentName} (${a.admissionNo}) — Room ${a.roomNumber}`}
                  onChange={(e, v) => setOutpassDlg({ ...outpassDlg, student: v })}
                  renderInput={params => <TextField {...params} label="Student (currently in hostel)" />} />
              </Grid>
              <Grid item xs={6}><TextField fullWidth type="date" label="Out date" InputLabelProps={{ shrink: true }} value={outpassDlg.outDate} onChange={e => setOutpassDlg({ ...outpassDlg, outDate: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth required type="date" label="Expected return" InputLabelProps={{ shrink: true }} value={outpassDlg.expectedReturnDate} onChange={e => setOutpassDlg({ ...outpassDlg, expectedReturnDate: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Destination" value={outpassDlg.destination} onChange={e => setOutpassDlg({ ...outpassDlg, destination: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Reason" value={outpassDlg.reason} onChange={e => setOutpassDlg({ ...outpassDlg, reason: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Contact number while out" value={outpassDlg.contactNumber} onChange={e => setOutpassDlg({ ...outpassDlg, contactNumber: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOutpassDlg(null)}>Cancel</Button><Button variant="contained" onClick={saveOutpass}>Create</Button></DialogActions>
      </Dialog>

      {/* Room change reject dialog */}
      <Dialog open={!!roomChangeReject} onClose={() => setRoomChangeReject(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject room change request</DialogTitle>
        <DialogContent dividers>
          {roomChangeReject && (
            <TextField fullWidth multiline rows={3} sx={{ mt: 0.5 }} label="Reason (optional, shown to the student)"
              value={roomChangeReject.remarks} onChange={e => setRoomChangeReject({ ...roomChangeReject, remarks: e.target.value })} />
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setRoomChangeReject(null)}>Cancel</Button><Button color="error" variant="contained" onClick={rejectRoomChange}>Reject</Button></DialogActions>
      </Dialog>

      {/* Generate hostel fee invoices dialog */}
      <Dialog open={!!feeGenDlg} onClose={() => setFeeGenDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate hostel fee invoices</DialogTitle>
        <DialogContent dividers>
          {feeGenDlg && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={6}><TextField fullWidth type="number" label="Month" inputProps={{ min: 1, max: 12 }} value={feeGenDlg.month} onChange={e => setFeeGenDlg({ ...feeGenDlg, month: Number(e.target.value) })} /></Grid>
              <Grid item xs={6}><TextField fullWidth type="number" label="Year" value={feeGenDlg.year} onChange={e => setFeeGenDlg({ ...feeGenDlg, year: Number(e.target.value) })} /></Grid>
              <Grid item xs={12}><Alert severity="info">Creates a PENDING invoice for every currently-allotted resident, using their room's monthly fee. Residents already invoiced for this month are skipped.</Alert></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setFeeGenDlg(null)}>Cancel</Button><Button variant="contained" onClick={generateFeeInvoices}>Generate</Button></DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
